import { format } from "date-fns"
import { treegridDateTimeFormat } from "../../utils"
import { DateTimeService } from "../../../services/date-time-service"
import { GanttZoom } from "../../types"
import { makeGanttBackground } from "./make-gantt-background"
import { CalendarSettingsViewModel } from "../../../calendar-settings/api/calendar-settings"

type MakeGanttColumnArgs = {
  Name?: string
  ganttBase: number | null
  ganttFinish: number | null
  showActualBar: boolean
  showBaselineBar: boolean
  defaultZoom: string
  zoomList: GanttZoom[]
  calendarSettings: CalendarSettingsViewModel
  enableTimeComponent: boolean
}

type ColumnConfig = Partial<TCol> & {
  _id: "gantt" // internal id used for mapping columns,
  Value?: unknown
  header?: string
}

export const makeGanttColumn = (args: MakeGanttColumnArgs): ColumnConfig => {
  const thisMoment = new Date()
  const { GanttBase, GanttFinish } = makeGanttBaseAndFinishDates({
    ganttBase: args.ganttBase,
    ganttFinish: args.ganttFinish,
    dateFormat: args.calendarSettings.dateFnsDateFormat,
    enableTimeComponent: args.enableTimeComponent,
  })

  const ganttColumn = {
    _id: "gantt" as const,
    Name: args.Name || "Gantt",
    Type: "Gantt",
    CanExport: 0, // Disables exporting of the Gantt chart
    GanttCount: 1, // Plan bar and actual bar
    /**
     * @link http://www.treegrid.com/Doc/GanttObjects.htm#CGanttEdit
     */
    GanttEdit: "Main,Dependency,DependencyTypes,DependencyLags",
    /** ---------------------------------------------------------------- */
    /** -------------------- Gantt background configuration ------------ */
    /** ---------------------------------------------------------------- */
    GanttMark: `${format(thisMoment, treegridDateTimeFormat)}`, // line indicating thisMoment
    GanttBackground: makeGanttBackground(args.calendarSettings), // background lines repeating every day and every weekend
    GanttBase,
    GanttFinish,
    GanttBaseCanEdit: 0,
    GanttFinishCanEdit: 0,
    GanttShowBounds: 2, // shows gantt base and gantt finish as a vertical line if they are defined
    /** ---------------------------------------------------------------- */
    /** -------------------- Gantt dependency configuration ------------ */
    /** ---------------------------------------------------------------- */
    GanttDependencyColor: 3,
    GanttAncestors: "ganttAncestors",
    GanttIncorrectDependencies: 0, // treegrid should not mark any dependencies as incorrect
    GanttCorrectDependencies: 0, // treegrid should not auto correct dependencies
    GanttCheckDependencies: 0, // treegrid should not check dependencies
    /** ---------------------------------------------------------------- */
    /** -------------------- Gantt zoom configuration ------------------ */
    /** ---------------------------------------------------------------- */
    GanttZoom: args.defaultZoom,
    GanttZoomList: args.zoomList as any,
    GanttSmoothZoom: 1, // controls whether to adjust the GanttWidth of each column with zoom
    /** ---------------------------------------------------------------- */
    /** -------------------- Gantt page configuration ------------------ */
    /** ---------------------------------------------------------------- */
    /** ---------------------------------------------------------------- */
    /** -------------------- Plan Bar configuration -------------------- */
    /** ---------------------------------------------------------------- */
    GanttStart: "plannedStartDate",
    GanttEnd: "plannedEndDate",
    GanttHeight: 16,
    GanttClass: "Blue",
    GanttComplete: "completionPercentage",
  }

  /** if actual bar is shown add actual bar to gantt column */
  if (args.showActualBar) {
    const GanttNumber = ganttColumn.GanttCount
    Object.assign(ganttColumn, {
      GanttCount: GanttNumber + 1, // Reset Gantt count
      /** ------------------------------------------------------------------ */
      /** -------------------- Actual Bar configuration -------------------- */
      /** ------------------------------------------------------------------ */
      [`GanttStart${GanttNumber}`]: "actualBarStartDate",
      [`GanttEnd${GanttNumber}`]: "actualBarEndDate",
      [`GanttTop${GanttNumber}`]: 0,
      [`GanttHeight${GanttNumber}`]: 3,
      [`GanttClass${GanttNumber}`]: "Gray",
    })
  }
  if (args.showBaselineBar) {
    const GanttNumber = ganttColumn.GanttCount
    Object.assign(ganttColumn, {
      GanttCount: GanttNumber + 1, // Reset Gantt count
      /** ------------------------------------------------------------------ */
      /** -------------------- Baseplan Bar configuration ------------------ */
      /** ------------------------------------------------------------------ */
      [`GanttStart${GanttNumber}`]: "basePlanStartDate",
      [`GanttEnd${GanttNumber}`]: "basePlanEndDate",
      [`GanttTop${GanttNumber}`]: 20,
      [`GanttHeight${GanttNumber}`]: 3,
      [`GanttClass${GanttNumber}`]: "Red",
      /** ------------------------------------------------------------------ */
    })
  }

  return ganttColumn
}

type MakeGanttBaseAndFinishDatesArgs = {
  ganttBase: number | null
  ganttFinish: number | null
  dateFormat: string
  enableTimeComponent: boolean
}

const makeGanttBaseAndFinishDates = (
  args: MakeGanttBaseAndFinishDatesArgs
): {
  GanttBase?: string
  GanttFinish?: string
} => {
  const { ganttBase, ganttFinish, dateFormat, enableTimeComponent } = args
  const dateTimeService = new DateTimeService({
    dateFormat,
    enableTimeComponent,
  })

  const ganttBaseDate = !enableTimeComponent
    ? ganttBase
      ? dateTimeService.startOfDay(new Date(ganttBase)).getTime()
      : undefined
    : ganttBase
  const timeZonedGanttBaseDate = ganttBaseDate
    ? dateTimeService.removeTimezoneOffset(new Date(ganttBaseDate), "UTC").getTime()
    : undefined

  const ganttFinishDate = !enableTimeComponent
    ? ganttFinish
      ? dateTimeService.endOfDay(new Date(ganttFinish)).getTime()
      : undefined
    : ganttFinish
  const timeZonedGanttFinishDate = ganttFinishDate
    ? dateTimeService.removeTimezoneOffset(new Date(ganttFinishDate), "UTC").getTime()
    : undefined

  const formattedGanttBaseDate = timeZonedGanttBaseDate
    ? dateTimeService.format(new Date(timeZonedGanttBaseDate), dateFormat)
    : undefined
  const formattedGanttFinishDate = timeZonedGanttFinishDate
    ? dateTimeService.format(new Date(timeZonedGanttFinishDate), dateFormat)
    : undefined

  return {
    GanttBase: formattedGanttBaseDate,
    GanttFinish: formattedGanttFinishDate,
  }
}
